import { useState, useRef, useEffect } from 'react';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'bot',
      text: 'Hello! I am SkillEx AI. How can I assist you today?',
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const typingIntervalRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const clearStreaming = () => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearStreaming();
  }, []);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container && shouldAutoScrollRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      shouldAutoScrollRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const streamText = (messageId, fullText, onDone) => {
    let i = 0;
    // ~25 chars per 100ms feels natural; for very long replies speed it up slightly
    const delay = fullText.length > 500 ? 10 : 18;

    typingIntervalRef.current = setInterval(() => {
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, text: fullText.slice(0, i) }
          : m
      ));
      i++;
      if (i > fullText.length) {
        clearStreaming();
        if (onDone) onDone();
      }
    }, delay);
  };

const handleSend = async () => {
    if (!inputValue.trim()) return;

  shouldAutoScrollRef.current = true;

    // If AI is still typing out the last answer, cancel the stream so we don't have competing updates
    clearStreaming();

    const nextId = messages.length + 1;

    const userMessage = {
      id: nextId,
      sender: 'user',
      text: inputValue,
      timestamp: new Date().toLocaleTimeString()
    };

    const pendingBotId = nextId + 1;

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const chatMessages = [
        ...messages.filter(message => message.id > 1 && message.text.trim()).map(message => ({
          role: message.sender === 'user' ? 'user' : 'assistant',
          content: message.text
        })),
        { role: 'user', content: userMessage.text }
      ];

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messages: chatMessages.slice(-20) })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Chat request failed');

      if (!data.reply) {
        throw new Error('Invalid API response format');
      }

      const replyText = data.reply;

      // First add an empty bot message, then stream text into it
      setMessages(prev => [...prev, {
        id: pendingBotId,
        sender: 'bot',
        text: '',
        timestamp: new Date().toLocaleTimeString()
      }]);

      // Hide the 3-dot "typing" indicator now that the bot bubble exists.
      // The "typing cursor" feeling comes from the streaming characters below.
      setIsTyping(false);

      streamText(pendingBotId, replyText);
    } catch (error) {
      const errorText = `I'm having trouble connecting to the AI service right now. Please contact SkillEx directly via WhatsApp at +86 18 2025 61437 or email at coachskillex@gmail.com for assistance.`;
      setMessages(prev => [...prev, {
        id: pendingBotId,
        sender: 'bot',
        text: '',
        timestamp: new Date().toLocaleTimeString()
      }]);
      setIsTyping(false);
      streamText(pendingBotId, errorText);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 md:top-[87%] md:right-2 z-40 px-4 md:px-6 py-3 rounded-full bg-gradient-to-r from-red-800 to-secondary text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105"
        >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="font-semibold text-sm hidden md:inline">SkillEx AI </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-4 left-2 right-2 md:bottom-6 md:right-6 md:left-auto z-40 w-auto md:w-96 h-[70vh] max-h-[calc(100vh-2rem)] min-h-0 bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideUp flex flex-col"
          style={{ height: 'min(70dvh, 40rem)', maxHeight: 'calc(100dvh - 2rem)' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-800 to-secondary px-6 py-4 text-white flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">SkillEx AI</h3>
                  <p className="text-xs text-white/70">Online - Available to help</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
                title="Close chat"
                className="w-9 h-9 flex-shrink-0 rounded-full bg-red-600 flex items-center justify-center hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              >
                <span className="text-lg font-bold leading-none" aria-hidden="true">X</span>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-4"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-primary text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                  <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-white/60' : 'text-gray-400'}`}>
                    {message.timestamp}
                  </p>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Input */}
          <div className="border-t border-gray-200 px-4 py-3 flex-shrink-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={4000}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;